import {ShippingApiService} from "./ShippingApiService";
import {ShippingUI} from "./ShippingUI";
import {ShippingErrorHandler} from "./ShippingErrorHandler";

declare var autoShippedWhenOpeningSO: boolean;
declare var isViewOnly: boolean;

export class ShippingActions {
    constructor(private ui: ShippingUI) {}

    async markAsShippedSilently(assetId: string | number): Promise<any> {
      if (isViewOnly) {
        ShippingErrorHandler.showWarning("This page is view only.");
        return;
      }

      if (!assetId) {
            ShippingErrorHandler.showError("Asset ID is required");
            return null;
        }

        try {
            const response = await ShippingApiService.markShipped(assetId);
            if (!response.success) {
                ShippingErrorHandler.showError(
                    response.message || "Failed to mark asset as shipped"
                );
                return null;
            }
            return response;
        } catch (error) {
            ShippingErrorHandler.logError("marking asset as shipped", error);
            const errorMessage = ShippingErrorHandler.handleApiError(
                error,
                "Error marking asset as shipped. Please try again."
            );
            ShippingErrorHandler.showError(errorMessage);
            return null;
        }
    }

    updateRowShippedStatus(assetId: string | number, epc?: string): void {
        const tableBody = $("#assets-table");
        const assetIdStr = String(assetId);

        tableBody.find("tr").each(function() {
            const row = $(this);
            const rowAssetId = row.attr("data-asset-id");

            // Match by asset ID (primary identifier)
            if (rowAssetId === assetIdStr) {
                // Update the data attribute
                row.attr("data-shipped", "true");

                // Update the shipped status icon
                const statusCell = row.find("td").first();
                statusCell.html('<i class="fa fa-check text-success"></i>');

                // Disable the action icons
                const actionsCell = row.find("td.sn-icons");
                actionsCell.find("a").each(function() {
                    $(this).addClass("disabled not-allowed").css("opacity", "0.5").off("click");
                });
            }
        });
    }

    async reloadAssetInTable(assetId?: string | number, epc?: string): Promise<void> {
        if (!assetId && !epc) {
            return;
        }

        try {
            // Use EPC for reloading since process_epc endpoint accepts EPC
            // If we have asset_id, we can still use EPC to fetch the updated asset
            const identifier = epc || (assetId ? String(assetId) : "");
            if (!identifier) {
                return;
            }

            const data = await ShippingApiService.fetchAssetByEpc(identifier);
            if (data.success && data.asset) {
                this.ui.upsertRow(data.asset, data.shopify_url);

                // If we have wave_order_assets (multi SO mode), reload all assets with the same EPC
                // This handles the case where multiple assets share the same EPC and need to be updated
                if (data.wave_order_assets && data.wave_order_assets.length > 0) {
                    const shippedEpc = data.asset.epc;
                    if (shippedEpc) {
                        // Find all rows in the table with the same EPC
                        const tableBody = $("#assets-table");
                        const rowsWithSameEpc: Array<{assetId: string, epc: string}> = [];

                        tableBody.find("tr").each(function() {
                            const row = $(this);
                            const rowEpc = row.attr("data-epc");
                            const rowAssetId = row.attr("data-asset-id");

                            // If EPC matches, collect the asset ID for reloading
                            if (rowEpc === shippedEpc && rowAssetId) {
                                rowsWithSameEpc.push({ assetId: rowAssetId, epc: rowEpc });
                            }
                        });

                        // Reload all assets with the same EPC by finding them in wave_order_assets
                        for (const rowInfo of rowsWithSameEpc) {
                            // Find the asset in wave_order_assets that matches this asset ID
                            const matchingAsset = data.wave_order_assets.find((wa: any) =>
                                String(wa.id) === String(rowInfo.assetId)
                            );

                            if (matchingAsset) {
                                this.ui.upsertRow(matchingAsset, matchingAsset.shopify_url || data.shopify_url);
                            }
                        }
                    }
                }
            }
        } catch (error) {
            // Silently fail - asset might not be found
        }
    }

    async reloadAllAssetsWithSameEpc(epc: string): Promise<void> {
        if (!epc) {
            return;
        }

        try {
            const data = await ShippingApiService.fetchAssetByEpc(epc);
            if (data.success && data.wave_order_assets && data.wave_order_assets.length > 0) {
                // Find all rows in the table with the same EPC
                const tableBody = $("#assets-table");
                const assetIdsInTable: Set<string> = new Set();

                tableBody.find("tr").each(function() {
                    const row = $(this);
                    const rowEpc = row.attr("data-epc");
                    const rowAssetId = row.attr("data-asset-id");

                    if (rowEpc === epc && rowAssetId) {
                        assetIdsInTable.add(rowAssetId);
                    }
                });

                // Update all assets with the same EPC from wave_order_assets
                // This ensures all assets sharing the same EPC are updated
                Array.from(assetIdsInTable).forEach((assetId) => {
                    const matchingAsset = data.wave_order_assets.find((wa: any) =>
                        String(wa.id) === String(assetId)
                    );

                    if (matchingAsset) {
                        this.ui.upsertRow(matchingAsset, matchingAsset.shopify_url || data.shopify_url);
                    }
                });
            }
        } catch (error) {
            // Silently fail
        }
    }

    openShopify(shopifyUrl?: string): void {
        if (shopifyUrl && shopifyUrl.trim() !== "") {
            window.open(shopifyUrl, "_blank");
        } else {
            ShippingErrorHandler.showWarning("No Shopify order found for this asset");
        }
    }
}

